/**
 * Push notification support — replaces utils/pushNotifications.ts
 *
 * Uses DryNotify's createPushManager for subscription lifecycle
 * and the DrySW service worker for receiving/displaying push messages.
 */

import { createPushManager } from '@drystuff/notify';
import type { DryPushManager } from '@drystuff/notify';
import { config } from './config';
import { http } from './client';

let pushManager: DryPushManager | null = null;

/**
 * Initialize push notification manager.
 * Call once from DryContext after SW registration.
 */
export function initPush(): DryPushManager | null {
  const vapidKey = config.push.vapidPublicKey;
  if (!vapidKey) {
    console.warn('[Push] No VAPID public key configured');
    return null;
  }

  pushManager = createPushManager({
    applicationServerKey: vapidKey,

    async onSubscribe(subscription: PushSubscription) {
      const json = subscription.toJSON();
      await http.request<any>({
        url: config.push.registrationEndpoint,
        method: 'POST',
        body: {
          device_id: getDeviceId(),
          endpoint: subscription.endpoint,
          p256dh_key: json.keys?.p256dh || '',
          auth_key: json.keys?.auth || '',
          user_agent: navigator.userAgent,
        },
      });
      console.log('[Push] Subscription saved to server');
      localStorage.setItem('push_subscribed', 'true');
    },

    async onUnsubscribe(subscription: PushSubscription) {
      try {
        await http.request<any>({
          url: config.push.registrationEndpoint,
          method: 'DELETE',
          body: { device_id: getDeviceId() },
        });
      } catch (err) {
        console.warn('[Push] Failed to unsubscribe on server:', err);
      }
      localStorage.removeItem('push_subscribed');
    },

    onPermissionDenied() {
      console.warn('[Push] Notification permission denied by user');
    },
  });

  return pushManager;
}

/**
 * Get the push manager instance.
 */
export function getPushManager(): DryPushManager | null {
  return pushManager;
}

/**
 * Dispose the push manager.
 */
export function disposePush(): void {
  pushManager?.dispose();
  pushManager = null;
}

// ─── Utility functions (backward-compatible with old pushNotifications.ts) ───

/**
 * Check if push notifications are supported.
 */
export function isPushSupported(): boolean {
  return pushManager?.isSupported() ?? (
    'serviceWorker' in navigator && 'PushManager' in window
  );
}

/**
 * Get current notification permission.
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/**
 * Check if currently subscribed.
 */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

// ─── Device ID management ───

function getDeviceId(): string {
  let deviceId = localStorage.getItem('push_device_id');
  if (!deviceId) {
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('push_device_id', deviceId);
  }
  return deviceId;
}
